import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Icon,
  IconButton,
  CircularProgress,
  alpha,
} from "@mui/material";
import { IESDoc } from "../../../../../app.interface";

interface BranchFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: IESDoc) => void;
  branch?: IESDoc | null;
  loading?: boolean;
}

export const BranchFormDialog: React.FC<BranchFormDialogProps> = ({
  open,
  onClose,
  onSave,
  branch,
  loading = false,
}) => {
  const [formData, setFormData] = React.useState<Partial<IESDoc>>({
    name: "",
    address: "",
    ...(branch || {}),
  });

  React.useEffect(() => {
    if (branch) {
      setFormData(branch);
    } else {
      setFormData({
        name: "",
        address: "",
      });
    }
  }, [branch]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData as IESDoc);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          background: "#0891B2",
          color: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Icon sx={{ fontSize: 20 }}>store</Icon>
            <Typography variant="subtitle1" fontWeight={600}>
              {branch ? "Edit Branch" : "Add New Branch"}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3 }}>
          <Box display="grid" gap={3}>
            <TextField
              label="Branch Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              InputLabelProps={{
                sx: { fontFamily: "Montserrat" },
              }}
              InputProps={{
                sx: { fontFamily: "Montserrat" },
                startAdornment: (
                  <Icon sx={{ mr: 1, color: "text.secondary" }}>store</Icon>
                ),
              }}
            />

            <TextField
              label="Address"
              fullWidth
              required
              multiline
              rows={3}
              value={formData.address}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, address: e.target.value }))
              }
              InputLabelProps={{
                sx: { fontFamily: "Montserrat" },
              }}
              InputProps={{
                sx: { fontFamily: "Montserrat" },
                startAdornment: (
                  <Icon sx={{ mr: 1, mt: 1.5, color: "text.secondary" }}>
                    location_on
                  </Icon>
                ),
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            bgcolor: "#F8FAFC",
            borderTop: "1px solid",
            borderColor: "divider",
            gap: 1,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            color="inherit"
            startIcon={<Icon>close</Icon>}
            disabled={loading}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              px: 2,
              py: 1,
              borderColor: "#E5E7EB",
              color: "#64748B",
              fontFamily: "Montserrat",
              "&:hover": {
                borderColor: "#94A3B8",
                bgcolor: "transparent",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Icon>save</Icon>
              )
            }
            sx={{
              borderRadius: 1,
              textTransform: "none",
              px: 3,
              py: 1,
              bgcolor: "#0891B2",
              fontFamily: "Montserrat",
              "&:hover": {
                bgcolor: "#0E7490",
              },
              "&.Mui-disabled": {
                bgcolor: alpha("#0891B2", 0.6),
                color: "white",
              },
            }}
          >
            {loading ? "Saving..." : "Save Branch"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
